<script lang="ts" setup>
import userStore from '@/store/UserStore'
import projectStore from '@/store/ProjectStore'
import formStore from '@/store/FormStore'
import { StepsController } from '@/controllers/StepsController'
import { ValidatorType } from '@/helper/ValidateDirective'
import FormElement from '@/components/FormElement.vue'
import { ProjectPropertyType } from '@/helper/Types'
import { api } from '@/services/ApiService'
import ComboBox from '@/components/ComboBox.vue'
import appStore from '@/store/AppStore'
import { computed, onMounted, ref } from 'vue'

const props = defineProps<{
  validator: ValidatorType
}>()

defineExpose({
  validator: props.validator
})

const matchingInstallerCities = ref<Record<string, string>[]>([])
const translatedMatchingInstallerCities = ref<string[]>([])

onMounted((): void => {
  onProjectPostalCodeInput()
})

function onProjectPostalCodeInput(): void {
  loadPostalCodeCities('installerPostalCode')?.then(postalCodeList => {
    matchingInstallerCities.value = postalCodeList
    userStore.validProjectPostalCodes = postalCodeList.length
      ? [formStore.data.installerPostalCode as keyof ProjectPropertyType]
      : []
    updateProjectCityOptions()
  })
}

function loadPostalCodeCities(propCode: keyof ProjectPropertyType): Promise<Record<string, string>[]> | void {
  if (!userStore.enableCityDropdown) {
    return
  }
  const val = (formStore.data[propCode] || '').toString().trim()
  return loadPostalCodesCities(val)
}

const projectCityOptions = computed(() => {
  return translatedMatchingInstallerCities.value.map(city => ({
    label: city,
    value: city
  }))
})

function updateProjectCityOptions(): void {
  if (!userStore.enableCityDropdown) {
    return
  }

  const val = (formStore.data.installerPostalCode || '').toString().trim()

  if (!val) {
    formStore.data.installerPostalCode = ''
    matchingInstallerCities.value = []
    return
  }

  const matchingCities = matchingInstallerCities.value.map(item => item[userStore.currentLanguage] as string)
  translatedMatchingInstallerCities.value = matchingCities

  if (!matchingInstallerCities.value.length) {
    formStore.data.installerCity = ''
  } else if (matchingInstallerCities.value.length === 1) {
    formStore.data.installerCity = matchingCities[0]
  }
}

async function loadPostalCodesCities(postalCode: string): Promise<Record<string, string>[]> {
  if (!userStore.enableCityDropdown) {
    return []
  }
  if (
    (userStore.postalCodeValidationPattern || '').trim() !== '' &&
    !new RegExp(userStore.postalCodeValidationPattern || '').test(postalCode)
  ) {
    return []
  }
  appStore.isLoading = true
  try {
    let res: Record<string, string>[]

    res = await api.loadProjectCities(postalCode)

    return res.sort((p1, p2) => {
      const localizedCityA = p1[userStore.currentLanguage]
      const localizedCityB = p2[userStore.currentLanguage]

      if (localizedCityA == localizedCityB) {
        return 0
      } else {
        return localizedCityA < localizedCityB ? -1 : 1
      }
    })
  } catch (e) {
    window.console.error(`Error fetching cities to postal codes "${postalCode}":`, e)
    return []
  } finally {
    appStore.isLoading = false
  }
}

</script>

<template>
  <form
    id="installer-information-form"
    action=""
    class="form steps__form"
    @submit.prevent="StepsController.nextClicked()"
  >
    <div class="form__section">
      <div class="">
        <div class="row form__row">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.installerCompanyName.formValidationRule"
              :error="validator.installerCompanyName?.error"
              :required="true"
              label-key="modal.download_configuration_installer_company_name"
            >
              <input
                id="installer-company-name"
                v-model="formStore.data.installerCompanyName"
                :placeholder="$t('modal.download_configuration_installer_company_name_placeholder')"
                class="firstfocus"
                data-hj-suppress
                type="text"
              >
            </FormElement>
          </div>
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.installerCompanyNumber.formValidationRule"
              :error="validator.installerCompanyNumber?.error"
              :required="true"
              label-key="modal.download_configuration_installer_company_number"
            >
              <input
                id="installer-company-number"
                v-model="formStore.data.installerCompanyNumber"
                :placeholder="$t('modal.download_configuration_installer_company_number_placeholder')"
                data-hj-suppress
                type="text"
              >
            </FormElement>
          </div>
        </div>
        <div class="row form__row">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.installerStreetName.formValidationRule"
              :error="validator.installerStreetName?.error"
              :required="true"
              label-key="modal.download_configuration_installer_street_name"
            >
              <input
                id="installer-street-name"
                v-model="formStore.data.installerStreetName"
                :placeholder="$t('modal.download_configuration_installer_street_name_placeholder')"
                data-hj-suppress
                type="text"
              >
            </FormElement>
          </div>
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.installerHouseNumber.formValidationRule"
              :error="validator.installerHouseNumber?.error"
              :required="false"
              label-key="modal.download_configuration_installer_house_number"
            >
              <input
                id="installer-house-number"
                v-model="formStore.data.installerHouseNumber"
                :placeholder="$t('modal.download_configuration_installer_house_number_placeholder')"
                data-hj-suppress
                type="text"
              >
            </FormElement>
          </div>
        </div>
        <div class="row form__row">
          <div class="col-md-6 project-postal-code">
            <FormElement
              v-validate="projectStore.projectProperties.installerPostalCode.formValidationRule"
              :error="validator.installerPostalCode?.error"
              :required="true"
              label-key="modal.download_configuration_installer_postal_code"
            >
              <input
                id="installer-postal-code"
                v-model="formStore.data.installerPostalCode"
                :placeholder="$t('modal.download_configuration_installer_postal_code_placeholder')"
                data-hj-whitelist
                name="installer-postal-code"
                type="text"
                @input="onProjectPostalCodeInput"
              >
            </FormElement>
          </div>
          <div class="col-md-6 project-city">
            <FormElement
              v-validate="projectStore.projectProperties.installerCity.formValidationRule"
              :error="validator.installerCity?.error"
              :required="true"
              label-key="modal.download_configuration_installer_city"
            >
              <ComboBox
                v-if="userStore.enableCityDropdown"
                id="installer-city"
                v-model="formStore.data.installerCity"
                :disabled="!projectCityOptions.length"
                :options="projectCityOptions"
              />

              <input
                v-else
                id="installer-city"
                v-model="formStore.data.installerCity"
                :placeholder="$t('modal.download_configuration_installer_city_placeholder')"
                data-hj-whitelist
                type="text"
              >
            </FormElement>
          </div>
        </div>
        <div class="row form__row">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.installerEmailAddress.formValidationRule"
              :error="validator.installerEmailAddress?.error"
              :required="projectStore.propertyIsRequired('installerEmailAddress')"
              label-key="modal.download_configuration_installer_email_address"
            >
              <input
                id="installer-email-address"
                v-model="formStore.data.installerEmailAddress"
                :placeholder="$t('modal.download_configuration_installer_email_address_placeholder')"
                data-hj-suppress
                type="text"
              >
            </FormElement>
          </div>
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.installerPhoneNumber.formValidationRule"
              :error="validator.installerPhoneNumber?.error"
              :required="projectStore.propertyIsRequired('installerPhoneNumber')"
              label-key="modal.download_configuration_installer_phone_number"
            >
              <input
                id="installer-phone-number"
                v-model="formStore.data.installerPhoneNumber"
                :placeholder="$t('modal.download_configuration_installer_phone_number_placeholder')"
                data-hj-suppress
                type="text"
              >
            </FormElement>
          </div>
        </div>
      </div>
    </div>
    <div class="form__section">
      <p class="installer-information">
        {{ $t('modal.download_configuration_installer_data') }}
      </p>
      <div class="row form__row">
        <div class="col-md-6">
          <FormElement
            v-validate="projectStore.projectProperties.installerFirstName.formValidationRule"
            :error="validator.installerFirstName?.error"
            :required="projectStore.propertyIsRequired('installerFirstName')"
            label-key="modal.download_configuration_installer_first_name"
          >
            <input
              id="installer-first-name"
              v-model="formStore.data.installerFirstName"
              :placeholder="$t('step_details.placeholder_first_name')"
              data-hj-suppress
              type="text"
            >
          </FormElement>
        </div>
        <div class="col-md-6">
          <FormElement
            v-validate="projectStore.projectProperties.installerLastName.formValidationRule"
            :error="validator.installerLastName?.error"
            :required="projectStore.propertyIsRequired('installerLastName')"
            label-key="modal.download_configuration_installer_last_name"
          >
            <input
              id="installer-last-name"
              v-model="formStore.data.installerLastName"
              :placeholder="$t('step_details.placeholder_last_name')"
              data-hj-suppress
              type="text"
            >
          </FormElement>
        </div>
      </div>
    </div>
    <input class="is--invisible" type="submit">
  </form>
</template>

<style lang="scss" scoped>
.steps {
  &__form {
    overflow: visible;
  }
}

.installer-information {
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.5rem;
  margin-top: 1rem;
}
</style>
